// src/components/common/ProgramVideo.tsx
//
// 지원금 관련 영상 영역.
//
// videoId 가 지정되어 있으면 유튜브 영상을 임베드하고,
// 없으면 해당 지원금명으로 공식 안내 영상을 찾아볼 수 있는 링크를 보여줍니다.
//
// ※ 영상 ID 는 실제로 존재하는 것만 넣어야 합니다.
//    src/data/supportPrograms.ts 의 각 항목에 videoId: 'xxxxxxxxxxx' 를 추가하세요.
//
import React, { useState } from 'react';
import Icon from './Icon';

interface ProgramVideoProps {
    title: string;
    /** 유튜브 영상 ID (11자) */
    videoId?: string;
    /** 영상 제목 */
    videoTitle?: string;
    /** 검색용 키워드 (기본값: 지원금 이름 + 신청방법) */
    searchQuery?: string;
}

const ProgramVideo: React.FC<ProgramVideoProps> = ({
    title,
    videoId,
    videoTitle,
    searchQuery,
}) => {
    const [playing, setPlaying] = useState(false);

    if (videoId) {
        return (
            <div className="gh-video">
                {playing ? (
                    <iframe
                        className="gh-video__frame"
                        src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`}
                        title={videoTitle ?? `${title} 안내 영상`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        loading="lazy"
                    />
                ) : (
                    // 클릭 전까지는 썸네일만 불러와 페이지 로딩 속도를 지킵니다
                    <button
                        type="button"
                        className="gh-video__poster"
                        onClick={() => setPlaying(true)}
                        aria-label={`${videoTitle ?? title} 영상 재생`}
                    >
                        <img
                            src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
                            alt=""
                            loading="lazy"
                        />
                        <span className="gh-video__play">
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path d="M8 5.5v13l11-6.5z" />
                            </svg>
                        </span>
                        {videoTitle && <span className="gh-video__caption">{videoTitle}</span>}
                    </button>
                )}
            </div>
        );
    }

    const q = encodeURIComponent(searchQuery ?? `${title} 신청방법`);

    return (
        <a
            className="gh-videolink"
            href={`https://www.youtube.com/results?search_query=${q}`}
            target="_blank"
            rel="noopener noreferrer"
        >
            <span className="gh-videolink__icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M8 5.5v13l11-6.5z" />
                </svg>
            </span>
            <span className="gh-videolink__body">
                <span className="gh-videolink__title">{title} 안내 영상 찾아보기</span>
                <span className="gh-videolink__desc">
                    소관 부처와 공공기관이 올린 신청 방법 영상을 유튜브에서 확인할 수 있습니다
                </span>
            </span>
            <Icon name="external" size={17} strokeWidth={2} className="gh-videolink__arrow" />
        </a>
    );
};

export default ProgramVideo;
